import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Solid amber app-header bar — present on every sub-page so the look is
 * consistent across the PWA. Sits below the OS status bar (handled via
 * `pt-safe-top`) and uses primary action amber (#F59E0B) as the surface,
 * matching the Save bar / FAB / brand-mark vocabulary.
 *
 *   ┌──────────────────────────────────────────┐
 *   │  ← {title}                       {right} │  amber-500, white text
 *   │     {subtitle}                           │
 *   └──────────────────────────────────────────┘
 *
 * Props:
 *   title      — main heading (string or node)
 *   subtitle   — optional secondary line
 *   icon       — optional Lucide icon component rendered before the title
 *                (e.g. a Camera glyph for "Capture Expense"). Use sparingly;
 *                title alone is usually enough.
 *   onBack     — back-button handler. Defaults to navigate(-1). Pass
 *                `false` (literal) to suppress the back button entirely
 *                (useful for top-level pages with no history).
 *   right      — optional node rendered on the right side (e.g. a kebab,
 *                an action icon button). Ensures the title stays centred.
 *
 * The header is intentionally not sticky/fixed — it scrolls away naturally,
 * which on mobile reads better than a permanently-pinned bar that eats
 * vertical space. If a screen genuinely needs a fixed header, wrap with
 * `sticky top-0 z-10` at the call site.
 */
export default function PageHeader({
  title,
  subtitle,
  icon: Icon,
  onBack,
  right,
}) {
  const navigate = useNavigate();
  const showBack = onBack !== false;
  const handleBack = typeof onBack === 'function' ? onBack : () => navigate(-1);

  return (
    // `mb-4` builds the gap between the amber bar and the first content
    // section into the component itself, so every page that uses PageHeader
    // gets consistent breathing room without each one having to remember
    // an mt-4. Side-bleed via `-mx-4 px-4` keeps the bar edge-to-edge while
    // preserving content-aligned title padding.
    <header className="bg-amber-500 text-white pt-safe-top -mx-4 px-4 mb-4">
      <div className="relative flex items-center py-3 min-h-touch">
        {showBack ? (
          <button
            type="button"
            onClick={handleBack}
            className="-ml-2 min-h-touch min-w-touch flex items-center justify-center text-white/90 hover:text-white relative z-10"
            aria-label="Back"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        ) : (
          <span className="w-touch shrink-0" aria-hidden="true" />
        )}

        {/* Title is absolutely positioned so it's true-horizontal-centred
            on the row regardless of what's on the sides. Earlier the
            back button's -ml-2 bleed shifted the flex centre column by
            ~4px, leaving the title visibly off-centre on every page.
            `pointer-events-none` lets edge taps still hit the back /
            right buttons underneath. `px-12` clears the touch-target
            zones so long titles truncate before colliding with them. */}
        <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center px-12">
          <h1 className="text-title font-bold leading-tight inline-flex items-center gap-2 max-w-full">
            {Icon && <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
            <span className="truncate">{title}</span>
          </h1>
          {subtitle && (
            <p className="text-caption text-white/80 truncate max-w-full">
              {subtitle}
            </p>
          )}
        </div>

        {/* Right slot pushed to end via ml-auto. When `right` is null we
            still render an empty touch-sized spacer so the row keeps a
            consistent visual rhythm and any z-index'd buttons line up
            with the back button on the left. */}
        <div className="ml-auto shrink-0 relative z-10">
          {right ?? <span className="w-touch shrink-0" aria-hidden="true" />}
        </div>
      </div>
    </header>
  );
}
